import React, { useEffect, useMemo, useState } from "react";
import { adminApi, toApiAbsoluteUrl } from "../../api/client";
import { Link } from "react-router-dom";
import { LinearProgress, Snackbar, Alert } from "@mui/material";
import {
  ChevronRight,
  FileText,
  X,
  CheckCircle,
  XCircle,
  Clock,
  UserCheck,
  UserX,
} from "lucide-react";

export default function Promoters() {
  const [promoters, setPromoters] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [verificationModal, setVerificationModal] = useState({
    open: false,
    promoter: null,
    action: null,
  });
  const [approvalModal, setApprovalModal] = useState({
    open: false,
    promoter: null,
    action: null,
  });
  const [verificationNotes, setVerificationNotes] = useState("");
  const [approvalNotes, setApprovalNotes] = useState("");
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({ q: "", page: 1, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  // Edit modal state
  const [editModal, setEditModal] = useState({
    open: false,
    promoter: null,
  });
  const [editForm, setEditForm] = useState({
    name: "",
    fatherName: "",
    bloodGroup: "",
    qualification: "",
    panNumber: "",
    address: {
      street: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
    },
  });
  const [editLoading, setEditLoading] = useState(false);
  const normalizedQuery = useMemo(
    () => (filters.q || "").trim().toLowerCase(),
    [filters.q]
  );

  const toAbsoluteUrl = toApiAbsoluteUrl;

  function formatBytes(bytes) {
    if (bytes === 0) return "0 B";
    if (!bytes || Number.isNaN(bytes)) return "-";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${
      sizes[i]
    }`;
  }

  function formatDate(iso) {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  }

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((p) => ({ ...p, q: searchText, page: 1 }));
    }, 200);
    return () => clearTimeout(handle);
  }, [searchText]);

  useEffect(() => {
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit]);

  async function runSearch(override = {}) {
    const merged = { ...filters, ...override };
    try {
      setLoading(true);
      const res = await adminApi.listPromoters({
        page: Number(merged.page || 1),
        limit: Number(merged.limit || 20),
      });
      setPromoters(res?.items || []);
      setTotal(res?.total || 0);
      setFilters((prev) => ({ ...prev, ...override }));
    } catch (_e) {
      console.error("Promoters API error:", _e);
      setPromoters([]);
      setTotal(0);
      const msg = _e?.data?.message || "Failed to load promoters";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  }

  const filteredPromoters = useMemo(() => {
    if (!normalizedQuery) return promoters;
    return (promoters || []).filter((p) => {
      const email = (p?.email || "").toLowerCase();
      const phone = (p?.phoneNumber || "").toLowerCase();
      const name = (p?.promoterProfile?.name || "").toLowerCase();
      const fatherName = (p?.promoterProfile?.fatherName || "").toLowerCase();
      const panNumber = (p?.promoterProfile?.panNumber || "").toLowerCase();
      const bloodGroup = (p?.promoterProfile?.bloodGroup || "").toLowerCase();
      const qualification = (
        p?.promoterProfile?.qualification || ""
      ).toLowerCase();
      const address = p?.promoterProfile?.address;
      const addressStr = address
        ? `${address.street || ""} ${address.city || ""} ${
            address.state || ""
          } ${address.pincode || ""}`.toLowerCase()
        : "";

      return (
        email.includes(normalizedQuery) ||
        phone.includes(normalizedQuery) ||
        name.includes(normalizedQuery) ||
        fatherName.includes(normalizedQuery) ||
        panNumber.includes(normalizedQuery) ||
        bloodGroup.includes(normalizedQuery) ||
        qualification.includes(normalizedQuery) ||
        addressStr.includes(normalizedQuery)
      );
    });
  }, [promoters, normalizedQuery]);

  // Payment verification functions
  const openVerificationModal = (promoter, action) => {
    setVerificationModal({ open: true, promoter, action });
    setVerificationNotes("");
  };

  const closeVerificationModal = () => {
    setVerificationModal({ open: false, promoter: null, action: null });
    setVerificationNotes("");
  };

  // Promoter approval functions
  const openApprovalModal = (promoter, action) => {
    setApprovalModal({ open: true, promoter, action });
    setApprovalNotes("");
  };

  const closeApprovalModal = () => {
    setApprovalModal({ open: false, promoter: null, action: null });
    setApprovalNotes("");
  };

  // Edit modal functions
  const openEditModal = (promoter) => {
    setEditModal({ open: true, promoter });
    setEditForm({
      name: promoter?.promoterProfile?.name || "",
      fatherName: promoter?.promoterProfile?.fatherName || "",
      bloodGroup: promoter?.promoterProfile?.bloodGroup || "",
      qualification: promoter?.promoterProfile?.qualification || "",
      panNumber: promoter?.promoterProfile?.panNumber || "",
      address: {
        street: promoter?.promoterProfile?.address?.street || "",
        city: promoter?.promoterProfile?.address?.city || "",
        state: promoter?.promoterProfile?.address?.state || "",
        pincode: promoter?.promoterProfile?.address?.pincode || "",
        country: promoter?.promoterProfile?.address?.country || "India",
      },
    });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, promoter: null });
    setEditForm({
      name: "",
      fatherName: "",
      bloodGroup: "",
      qualification: "",
      panNumber: "",
      address: {
        street: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
      },
    });
  };

  const handleEditSubmit = async () => {
    if (!editModal.promoter) return;

    setEditLoading(true);
    try {
      // TODO: Add API call to update promoter
      await adminApi.updatePromoter(editModal.promoter.id, editForm);

      setSnackbar({
        open: true,
        message: "Promoter updated successfully",
        severity: "success",
      });

      closeEditModal();
      await runSearch();
    } catch (error) {
      console.error("Update promoter error:", error);
      setSnackbar({
        open: true,
        message: error?.data?.message || "Failed to update promoter",
        severity: "error",
      });
    } finally {
      setEditLoading(false);
    }
  };

  const handlePaymentVerification = async () => {
    if (!verificationModal.promoter || !verificationModal.action) return;

    setVerificationLoading(true);
    try {
      const isVerified = verificationModal.action === "verify";
      await adminApi.verifyPayment(
        verificationModal.promoter.id,
        isVerified,
        verificationNotes
      );

      setSnackbar({
        open: true,
        message: `Payment ${isVerified ? "verified" : "rejected"} successfully`,
        severity: "success",
      });

      // Refresh the promoters list
      await runSearch();
      closeVerificationModal();
    } catch (error) {
      console.error("Payment verification error:", error);
      setSnackbar({
        open: true,
        message: error?.data?.message || "Failed to verify payment",
        severity: "error",
      });
    } finally {
      setVerificationLoading(false);
    }
  };

  const handlePromoterApproval = async () => {
    if (!approvalModal.promoter || !approvalModal.action) return;

    setApprovalLoading(true);
    try {
      if (approvalModal.action === "approve") {
        await adminApi.approvePromoter(approvalModal.promoter.id);
        // Also verify payment when approving promoter
        await adminApi.verifyPayment(
          approvalModal.promoter.id,
          true,
          approvalNotes
        );
      } else if (approvalModal.action === "reject") {
        await adminApi.rejectPromoter(approvalModal.promoter.id, approvalNotes);
      }

      setSnackbar({
        open: true,
        message: `Promoter ${
          approvalModal.action === "approve" ? "approved" : "rejected"
        } successfully`,
        severity: "success",
      });

      // Refresh the promoters list
      await runSearch();
      closeApprovalModal();
    } catch (error) {
      console.error("Promoter approval error:", error);
      setSnackbar({
        open: true,
        message:
          error?.data?.message || `Failed to ${approvalModal.action} promoter`,
        severity: "error",
      });
    } finally {
      setApprovalLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-4 text-sm text-gray-600 flex items-center gap-1">
        <Link to="/dashboard" className="hover:text-gray-800">
          Dashboard
        </Link>
        <ChevronRight className="h-4 w-4 opacity-60" />
        <span className="text-gray-800 font-medium">Promoters</span>
      </div>

      {/* Search bar */}
      <div className="bg-white shadow rounded p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          type="text"
          placeholder="Search name, email, phone, PAN, father's name, or address"
          className="border p-2 rounded"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <div className="flex items-center gap-2">
          <button
            className="px-4 py-2 bg-gray-600 text-white rounded"
            onClick={() => {
              setSearchText("");
              setFilters({ q: "", page: 1, limit: filters.limit });
              runSearch({ page: 1 });
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Responsive table */}
      <div className="overflow-x-auto">
        <table className="w-full text-center bg-white shadow rounded min-w-[1400px]">
          <thead>
            <tr>
              <th className="p-2 border">Name</th>
              <th className="p-2 border">Email</th>
              <th className="p-2 border">Phone</th>
              <th className="p-2 border">Father's Name</th>
              <th className="p-2 border">PAN</th>
              <th className="p-2 border">Blood Group</th>
              <th className="p-2 border">Qualification</th>
              <th className="p-2 border">Address</th>
              <th className="p-2 border">Channel Partner</th>
              <th className="p-2 border">Status</th>
              <th className="p-2 border">Payment</th>
              <th className="p-2 border">Commission</th>
              <th className="p-2 border">Documents</th>
              <th className="p-2 border">Created</th>
              <th className="p-2 border">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredPromoters.map((p) => {
              const name = p?.promoterProfile?.name || "-";
              const docs = p?.promoterProfile?.documents || [];
              const address = p?.promoterProfile?.address;
              const addressStr = address
                ? `${address.street || ""}${
                    address.city ? `, ${address.city}` : ""
                  }${address.state ? `, ${address.state}` : ""}${
                    address.pincode ? ` - ${address.pincode}` : ""
                  }`.trim()
                : "-";

              return (
                <tr key={p.id}>
                  <td className="p-2 border">
                    {name !== "-" ? (
                      <button
                        className="text-blue-600 hover:text-blue-800 underline"
                        onClick={() => setSelectedRecord(p)}
                        title="View details"
                      >
                        {name}
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="p-2 border text-sm">{p.email}</td>
                  <td className="p-2 border text-sm">{p.phoneNumber || "-"}</td>
                  <td className="p-2 border text-sm">
                    {p?.promoterProfile?.fatherName || "-"}
                  </td>
                  <td className="p-2 border text-sm">
                    {p?.promoterProfile?.panNumber || "-"}
                  </td>
                  <td className="p-2 border text-sm">
                    {p?.promoterProfile?.bloodGroup || "-"}
                  </td>
                  <td className="p-2 border text-sm">
                    {p?.promoterProfile?.qualification || "-"}
                  </td>
                  <td
                    className="p-2 border text-sm max-w-[200px] truncate"
                    title={addressStr}
                  >
                    {addressStr}
                  </td>
                  <td className="p-2 border">
                    {p?.channelPartnerName ? (
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {p.channelPartnerName}
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                        Independent
                      </span>
                    )}
                  </td>
                  <td className="p-2 border">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        p?.promoterProfile?.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : p?.promoterProfile?.status === "pending_approval"
                          ? "bg-yellow-100 text-yellow-800"
                          : p?.promoterProfile?.status ===
                            "pending_verification"
                          ? "bg-blue-100 text-blue-800"
                          : p?.promoterProfile?.status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {p?.promoterProfile?.status || "Unknown"}
                    </span>
                  </td>
                  <td className="p-2 border">
                    <div className="flex flex-col gap-1">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          p?.promoterProfile?.paymentDetails?.paymentStatus ===
                          "verified"
                            ? "bg-green-100 text-green-800"
                            : p?.promoterProfile?.paymentDetails
                                ?.paymentStatus === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : p?.promoterProfile?.paymentDetails
                                ?.paymentStatus === "rejected"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {p?.promoterProfile?.paymentDetails?.paymentStatus ||
                          "Unpaid"}
                      </span>
                      {p?.promoterProfile?.paymentDetails?.utrId && (
                        <span className="text-xs text-gray-600">
                          UTR: {p.promoterProfile.paymentDetails.utrId}
                        </span>
                      )}
                      {p?.promoterProfile?.paymentDetails?.amount && (
                        <span className="text-xs text-gray-600">
                          ₹{p.promoterProfile.paymentDetails.amount}
                        </span>
                      )}
                      {/* Payment Verification Buttons */}
                      {p?.promoterProfile?.paymentDetails?.paymentStatus ===
                        "pending" && (
                        <div className="flex gap-1 mt-1">
                          <button
                            onClick={() => openVerificationModal(p, "verify")}
                            className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors flex items-center gap-1"
                            title="Verify Payment"
                          >
                            <CheckCircle size={12} />
                            Verify
                          </button>
                          <button
                            onClick={() => openVerificationModal(p, "reject")}
                            className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors flex items-center gap-1"
                            title="Reject Payment"
                          >
                            <XCircle size={12} />
                            Reject
                          </button>
                        </div>
                      )}

                      {/* Promoter Approval Buttons (approves both profile and payment) */}
                      {p?.promoterProfile?.status === "pending_approval" && (
                        <div className="flex gap-1 mt-1">
                          <button
                            onClick={() => openApprovalModal(p, "approve")}
                            className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors flex items-center gap-1"
                            title="Approve Promoter"
                          >
                            <UserCheck size={12} />
                            Approve
                          </button>
                          <button
                            onClick={() => openApprovalModal(p, "reject")}
                            className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors flex items-center gap-1"
                            title="Reject Promoter"
                          >
                            <UserX size={12} />
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-2 border">
                    {p?.promoterProfile?.commissionRate
                      ? `${p.promoterProfile.commissionRate}%`
                      : "-"}
                  </td>
                  <td className="p-2 border">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        docs.length > 0
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {docs.length} docs
                    </span>
                  </td>
                  <td className="p-2 border text-sm">
                    {formatDate(p?.promoterProfile?.createdAt)}
                  </td>
                  <td className="p-2 border">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedRecord(p)}
                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
                      >
                        View
                      </button>
                      <button
                        onClick={() => openEditModal(p)}
                        className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-sm"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredPromoters.length === 0 && (
              <tr>
                <td className="p-6 text-gray-500 text-center" colSpan={15}>
                  No promoters found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between p-3 flex-wrap gap-2">
        <div className="text-sm text-gray-600">Total: {total}</div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            disabled={filters.page <= 1}
            onClick={() =>
              runSearch({ page: Math.max(1, (filters.page || 1) - 1) })
            }
          >
            Prev
          </button>
          <span className="text-sm">Page {filters.page}</span>
          <button
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            disabled={promoters.length < filters.limit}
            onClick={() => runSearch({ page: (filters.page || 1) + 1 })}
          >
            Next
          </button>
          <select
            className="ml-2 border p-1 rounded text-sm"
            value={filters.limit}
            onChange={(e) =>
              runSearch({ limit: Number(e.target.value), page: 1 })
            }
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        </div>
      </div>

      {/* Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Promoter Details</h3>
              <button
                onClick={() => setSelectedRecord(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="font-semibold">Basic Information</p>
                <p>Email: {selectedRecord.email}</p>
                <p>Phone: {selectedRecord.phoneNumber || "-"}</p>
                <p>Role: {selectedRecord.role}</p>
                <p>
                  Payment Status:{" "}
                  {selectedRecord.promoterProfile?.paymentDetails
                    ?.paymentStatus || "-"}
                </p>
                <p>
                  UTR ID:{" "}
                  {selectedRecord.promoterProfile?.paymentDetails?.utrId || "-"}
                </p>
                <p>
                  Payment Amount:{" "}
                  {selectedRecord.promoterProfile?.paymentDetails?.amount
                    ? `₹${selectedRecord.promoterProfile.paymentDetails.amount}`
                    : "-"}
                </p>
              </div>

              {selectedRecord.promoterProfile && (
                <>
                  <div>
                    <p className="font-semibold">Personal Information</p>
                    <p>Name: {selectedRecord.promoterProfile.name}</p>
                    <p>
                      Father's Name:{" "}
                      {selectedRecord.promoterProfile.fatherName || "N/A"}
                    </p>
                    <p>
                      Blood Group:{" "}
                      {selectedRecord.promoterProfile.bloodGroup || "N/A"}
                    </p>
                    <p>
                      Qualification:{" "}
                      {selectedRecord.promoterProfile.qualification || "N/A"}
                    </p>
                    <p>
                      PAN Number:{" "}
                      {selectedRecord.promoterProfile.panNumber || "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">Address</p>
                    <p>
                      {selectedRecord.promoterProfile.address?.street || ""}
                      {selectedRecord.promoterProfile.address?.city &&
                        `, ${selectedRecord.promoterProfile.address.city}`}
                      {selectedRecord.promoterProfile.address?.state &&
                        `, ${selectedRecord.promoterProfile.address.state}`}
                      {selectedRecord.promoterProfile.address?.pincode &&
                        ` - ${selectedRecord.promoterProfile.address.pincode}`}
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">Payment Details</p>
                    <p>
                      Amount:{" "}
                      {selectedRecord.promoterProfile.paymentDetails?.amount
                        ? `₹${selectedRecord.promoterProfile.paymentDetails.amount}`
                        : "N/A"}
                    </p>
                    <p>
                      UTR ID:{" "}
                      {selectedRecord.promoterProfile.paymentDetails?.utrId ||
                        "N/A"}
                    </p>
                    <p>
                      Payment Status:{" "}
                      {selectedRecord.promoterProfile.paymentDetails
                        ?.paymentStatus || "N/A"}
                    </p>
                    <p>
                      Verified At:{" "}
                      {selectedRecord.promoterProfile.paymentDetails
                        ?.paymentVerifiedAt
                        ? new Date(
                            selectedRecord.promoterProfile.paymentDetails.paymentVerifiedAt
                          ).toLocaleString()
                        : "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">Status Information</p>
                    <p>Status: {selectedRecord.promoterProfile.status}</p>
                    <p>
                      Commission Rate:{" "}
                      {selectedRecord.promoterProfile.commissionRate
                        ? `${selectedRecord.promoterProfile.commissionRate}%`
                        : "N/A"}
                    </p>
                    <p>
                      Total Providers Created:{" "}
                      {selectedRecord.promoterProfile.totalProvidersCreated ||
                        0}
                    </p>
                    <p>
                      Total Listings:{" "}
                      {selectedRecord.promoterProfile
                        .totalListingsUnderPromoter || 0}
                    </p>
                  </div>

                  <div className="mt-4">
                    <h4 className="font-semibold text-lg mb-3 text-red-800">
                      Documents (
                      {(() => {
                        const docs = selectedRecord.promoterProfile.documents;
                        if (Array.isArray(docs)) {
                          return docs.length;
                        } else if (docs && typeof docs === "object") {
                          return Object.keys(docs).length;
                        }
                        return 0;
                      })()}
                      )
                    </h4>
                    <div className="mt-3 max-h-72 overflow-y-auto border rounded">
                      {(() => {
                        const docs = selectedRecord.promoterProfile.documents;
                        const hasDocuments = Array.isArray(docs)
                          ? docs.length > 0
                          : docs &&
                            typeof docs === "object" &&
                            Object.keys(docs).length > 0;

                        if (!hasDocuments) {
                          return (
                            <div className="p-4 text-sm text-gray-500">
                              No documents uploaded
                            </div>
                          );
                        }

                        // Handle array format (full document objects)
                        if (Array.isArray(docs)) {
                          return (
                            <table className="w-full text-left text-sm min-w-[600px]">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="p-2 border">Preview</th>
                                  <th className="p-2 border">Name</th>
                                  <th className="p-2 border">Type</th>
                                  <th className="p-2 border">Size</th>
                                  <th className="p-2 border">Uploaded</th>
                                  <th className="p-2 border">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {docs.map((d) => {
                                  const isImage = (
                                    d?.mimeType || ""
                                  ).startsWith("image/");
                                  return (
                                    <tr key={d.id}>
                                      <td className="p-2 border align-middle">
                                        {isImage ? (
                                          <button
                                            className="inline-flex items-center"
                                            onClick={() =>
                                              window.open(
                                                toAbsoluteUrl(d.url),
                                                "_blank",
                                                "noopener"
                                              )
                                            }
                                            title="Open image"
                                          >
                                            <img
                                              src={toAbsoluteUrl(d.url)}
                                              alt={
                                                d.originalName ||
                                                d.kind ||
                                                "document"
                                              }
                                              className="h-12 w-12 object-cover rounded"
                                            />
                                          </button>
                                        ) : (
                                          <div className="flex items-center text-gray-600">
                                            <FileText
                                              size={18}
                                              className="mr-1"
                                            />
                                            <span>File</span>
                                          </div>
                                        )}
                                      </td>
                                      <td className="p-2 border align-middle">
                                        {d.originalName || d.kind || "-"}
                                      </td>
                                      <td className="p-2 border align-middle">
                                        {d.mimeType || "-"}
                                      </td>
                                      <td className="p-2 border align-middle">
                                        {formatBytes(d.size)}
                                      </td>
                                      <td className="p-2 border align-middle">
                                        {formatDate(d.uploadedAt)}
                                      </td>
                                      <td className="p-2 border align-middle">
                                        <div className="flex items-center gap-2">
                                          <button
                                            className="px-2 py-1 text-blue-600 hover:text-blue-800 underline"
                                            onClick={() =>
                                              window.open(
                                                toAbsoluteUrl(d.url),
                                                "_blank",
                                                "noopener"
                                              )
                                            }
                                          >
                                            Open
                                          </button>
                                          <a
                                            className="px-2 py-1 text-gray-700 hover:text-gray-900 underline"
                                            href={toAbsoluteUrl(d.url)}
                                            download={d.originalName || true}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            Download
                                          </a>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          );
                        }

                        // Handle object format (document IDs)
                        if (docs && typeof docs === "object") {
                          const documentTypes = {
                            experienceDocuments: "Experience Documents",
                            panCardImage: "PAN Card",
                            kycDocument: "KYC Document",
                            kycImage: "KYC Image",
                            cvUpload: "CV Upload",
                            photoUpload: "Photo Upload",
                          };

                          return (
                            <table className="w-full text-left text-sm min-w-[600px]">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="p-2 border">Document Type</th>
                                  <th className="p-2 border">Document ID</th>
                                  <th className="p-2 border">Status</th>
                                  <th className="p-2 border">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(docs).map(([key, docId]) => (
                                  <tr key={key}>
                                    <td className="p-2 border align-middle">
                                      <div className="flex items-center text-gray-600">
                                        <FileText size={18} className="mr-2" />
                                        <span className="font-medium">
                                          {documentTypes[key] || key}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="p-2 border align-middle text-sm">
                                      {docId}
                                    </td>
                                    <td className="p-2 border align-middle">
                                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                        Uploaded
                                      </span>
                                    </td>
                                    <td className="p-2 border align-middle">
                                      <div className="flex items-center gap-2">
                                        <button
                                          className="px-2 py-1 text-blue-600 hover:text-blue-800 underline"
                                          onClick={() =>
                                            window.open(
                                              toAbsoluteUrl(
                                                `/uploads/${docId}`
                                              ),
                                              "_blank",
                                              "noopener"
                                            )
                                          }
                                        >
                                          View
                                        </button>
                                        <a
                                          className="px-2 py-1 text-gray-700 hover:text-gray-900 underline"
                                          href={toAbsoluteUrl(
                                            `/uploads/${docId}`
                                          )}
                                          download
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          Download
                                        </a>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          );
                        }

                        return (
                          <div className="p-4 text-sm text-gray-500">
                            No documents uploaded
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loader */}
      {loading && (
        <div className="fixed bottom-0 left-0 right-0 z-50">
          <LinearProgress />
        </div>
      )}

      {/* Payment Verification Modal */}
      {verificationModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">
                {verificationModal.action === "verify"
                  ? "Verify Payment"
                  : "Reject Payment"}
              </h3>
              <button
                onClick={closeVerificationModal}
                className="text-gray-500 hover:text-gray-700"
                disabled={verificationLoading}
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded">
                <p className="font-semibold">
                  {verificationModal.promoter?.promoterProfile?.name}
                </p>
                <p className="text-sm text-gray-600">
                  {verificationModal.promoter?.email}
                </p>
                <p className="text-sm text-gray-600">
                  UTR ID:{" "}
                  {verificationModal.promoter?.promoterProfile?.paymentDetails
                    ?.utrId || "N/A"}
                </p>
                <p className="text-sm text-gray-600">
                  Amount: ₹
                  {verificationModal.promoter?.promoterProfile?.paymentDetails
                    ?.amount || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {verificationModal.action === "verify"
                    ? "Verification Notes (Optional)"
                    : "Rejection Reason"}
                </label>
                <textarea
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder={
                    verificationModal.action === "verify"
                      ? "Add any notes about the verification..."
                      : "Please provide a reason for rejection..."
                  }
                  className="w-full border p-3 rounded-md resize-none"
                  rows={3}
                  disabled={verificationLoading}
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={closeVerificationModal}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                  disabled={verificationLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePaymentVerification}
                  className={`px-4 py-2 rounded text-white transition-colors flex items-center gap-2 ${
                    verificationModal.action === "verify"
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-red-600 hover:bg-red-700"
                  } ${
                    verificationLoading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={verificationLoading}
                >
                  {verificationLoading ? (
                    <>
                      <Clock size={16} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      {verificationModal.action === "verify" ? (
                        <>
                          <CheckCircle size={16} />
                          Verify Payment
                        </>
                      ) : (
                        <>
                          <XCircle size={16} />
                          Reject Payment
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Promoter Approval Modal */}
      {approvalModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">
                {approvalModal.action === "approve"
                  ? "Approve Promoter (Profile + Payment)"
                  : "Reject Promoter"}
              </h3>
              <button
                onClick={closeApprovalModal}
                className="text-gray-500 hover:text-gray-700"
                disabled={approvalLoading}
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded">
                <p className="font-semibold">
                  {approvalModal.promoter?.promoterProfile?.name}
                </p>
                <p className="text-sm text-gray-600">
                  {approvalModal.promoter?.email}
                </p>
                <p className="text-sm text-gray-600">
                  Status:{" "}
                  {approvalModal.promoter?.promoterProfile?.status || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {approvalModal.action === "approve"
                    ? "Approval Notes (Optional)"
                    : "Rejection Reason"}
                </label>
                <textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder={
                    approvalModal.action === "approve"
                      ? "Add any notes about the approval..."
                      : "Please provide a reason for rejection..."
                  }
                  className="w-full border p-3 rounded-md resize-none"
                  rows={3}
                  disabled={approvalLoading}
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={closeApprovalModal}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                  disabled={approvalLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePromoterApproval}
                  className={`px-4 py-2 rounded text-white transition-colors flex items-center gap-2 ${
                    approvalModal.action === "approve"
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-red-600 hover:bg-red-700"
                  } ${approvalLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={approvalLoading}
                >
                  {approvalLoading ? (
                    <>
                      <Clock size={16} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      {approvalModal.action === "approve" ? (
                        <>
                          <UserCheck size={16} />
                          Approve Promoter
                        </>
                      ) : (
                        <>
                          <UserX size={16} />
                          Reject Promoter
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Edit Promoter</h3>
              <button
                onClick={closeEditModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    disabled={editLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Father's Name
                  </label>
                  <input
                    type="text"
                    value={editForm.fatherName}
                    onChange={(e) =>
                      setEditForm({ ...editForm, fatherName: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    disabled={editLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Blood Group
                  </label>
                  <select
                    value={editForm.bloodGroup}
                    onChange={(e) =>
                      setEditForm({ ...editForm, bloodGroup: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    disabled={editLoading}
                  >
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Qualification
                  </label>
                  <input
                    type="text"
                    value={editForm.qualification}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        qualification: e.target.value,
                      })
                    }
                    className="w-full border p-2 rounded-md"
                    disabled={editLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PAN Number
                  </label>
                  <input
                    type="text"
                    value={editForm.panNumber}
                    onChange={(e) =>
                      setEditForm({ ...editForm, panNumber: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    disabled={editLoading}
                  />
                </div>
              </div>

              {/* Address Information */}
              <div>
                <h4 className="text-lg font-semibold mb-3">
                  Address Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address
                    </label>
                    <input
                      type="text"
                      value={editForm.address.street}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          address: {
                            ...editForm.address,
                            street: e.target.value,
                          },
                        })
                      }
                      className="w-full border p-2 rounded-md"
                      disabled={editLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={editForm.address.city}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          address: {
                            ...editForm.address,
                            city: e.target.value,
                          },
                        })
                      }
                      className="w-full border p-2 rounded-md"
                      disabled={editLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      value={editForm.address.state}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          address: {
                            ...editForm.address,
                            state: e.target.value,
                          },
                        })
                      }
                      className="w-full border p-2 rounded-md"
                      disabled={editLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pincode
                    </label>
                    <input
                      type="text"
                      value={editForm.address.pincode}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          address: {
                            ...editForm.address,
                            pincode: e.target.value,
                          },
                        })
                      }
                      className="w-full border p-2 rounded-md"
                      disabled={editLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country
                    </label>
                    <input
                      type="text"
                      value={editForm.address.country}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          address: {
                            ...editForm.address,
                            country: e.target.value,
                          },
                        })
                      }
                      className="w-full border p-2 rounded-md"
                      disabled={editLoading}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                <button
                  onClick={closeEditModal}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  className={`px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center gap-2 ${
                    editLoading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={editLoading}
                >
                  {editLoading ? (
                    <>
                      <Clock size={16} className="animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Promoter"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar({ open: false, message: "", severity: "error" })
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() =>
            setSnackbar({ open: false, message: "", severity: "error" })
          }
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
